import { Component } from '@angular/core';
import { Routes } from '@angular/router';

import { reportDestinationGuard } from 'src/app/Services/report-destination.guard';
import {
  CO2_DESTINATION,
  ESG_DESTINATION,
  PROCUREMENT_PERFORMANCE_DESTINATION,
  PROCUREMENT_RISK_DESTINATION,
  SPEND_DASHBOARD_DESTINATION,
  SPEND_INTELLIGENCE_DESTINATION,
} from './report-view.destinations';
import { ReportViewConfig } from './report-view.model';

/**
 * The guarded report destinations added on top of `/dashboard`.
 *
 * Kept here rather than inline in app.routes.ts because every one of them is the
 * same route with a different config — spelling them out five times invites the
 * next one to drift.
 *
 * `path` is load-bearing twice over: it is the URL, and `reportDestinationGuard`
 * matches it against `tblPage.Link` to decide whether this tenant has the page.
 * Renaming a path without renaming the row disables the page.
 */
const DESTINATIONS: ReadonlyArray<{ path: string; config: ReportViewConfig }> = [
  { path: 'esg-analytics', config: ESG_DESTINATION },
  { path: 'co2-analytics', config: CO2_DESTINATION },
  { path: 'procurement-performance-cockpit', config: PROCUREMENT_PERFORMANCE_DESTINATION },
  { path: 'procurement-risk-cockpit', config: PROCUREMENT_RISK_DESTINATION },
  { path: 'spend-intelligence', config: SPEND_INTELLIGENCE_DESTINATION },
];

/**
 * Placeholder each destination activates. The router needs a component per child
 * route; everything visible is rendered by the parent `ReportViewComponent`.
 */
@Component({
  selector: 'app-report-destination',
  standalone: true,
  template: '',
})
export class ReportDestinationComponent { }

/**
 * Every destination is a child of one componentful parent, so moving between them
 * keeps the same `ReportViewComponent` — and its embedded report — alive instead
 * of rebuilding the iframe and re-fetching the embed token on every click. They
 * all embed the same report; a switch only re-cuts its pages.
 *
 * The parent consumes no URL segment, so every URL is unchanged, and an empty or
 * unknown URL matches no child and falls through to the routes after this one.
 *
 * `/dashboard` is deliberately unguarded, as it always has been — it is the
 * redirect target of the empty URL and of `reportDestinationGuard`, so a guard
 * here would bounce a mistyped URL into a redirect loop, and a tenant without
 * RBAC would be locked out of its own dashboard.
 */
export const REPORT_DESTINATION_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () => import('./report-view.component').then((m) => m.ReportViewComponent),
    children: [
      {
        path: 'dashboard',
        component: ReportDestinationComponent,
        data: { reportView: SPEND_DASHBOARD_DESTINATION },
      },
      ...DESTINATIONS.map(({ path, config }) => ({
        path,
        component: ReportDestinationComponent,
        canActivate: [reportDestinationGuard],
        data: { reportView: config },
      })),
    ],
  },
];
